import moment from "moment";
import { useCallback } from "react";
import { PiCircle } from "react-icons/pi";
import { useParams } from "react-router-dom";
import { Text, Title, cn } from "rizzui";
import useFetch from "../../../../../hooks/useFetch";
import useFilters from "../../../../../hooks/useFilters";
import { ContractService } from "../../../../../services/contract/contractService";
import { ContractApiResponse } from "../../../../../type/entities/contract-type";
import ContractProfileLayoutWrapper from "../../ContractProfileLayoutWrapper";

const ContractScoresHistory: React.FC = () => {

    // HOOKS

    const { id = '' } = useParams<{ id: string }>();
    const { filters } = useFilters({ contract: id }, [{ field: 'createdAt', order: 'desc' }]);
    const service = new ContractService();

    // FUNCTIONS

    // -------------------------------------------------------------------
    /**
     * @ES OBTIENE LOS DATOS DEL CONTRATO POR ID
     * @EN GETS THE CONTRACT DATA BY ID
     */
    // -------------------------------------------------------------------
    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getContractById(id);
        return response.getResponseData() as ContractApiResponse;
    }, [id]));
    // -------------------------------------------------------------------

    // -------------------------------------------------------------------
    /**
     * @ES OBTIENE LOS DATOS DEL HISTORIAL DE PUNTUACIÓN DEL CONTRATO POR ID
     * @EN GETS THE CONTRACT SCORE HISTORY DATA BY ID
     */
    // -------------------------------------------------------------------
    const [timeline, loadingTimeline] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = (await service.listContractPointsHistory(filters)).getResponseData();
        return response || [];
    }, [id]));
    // -------------------------------------------------------------------

    // CALCULATE NET SCORE
    const netScore = timeline?.data?.reduce((acc: number, item: any) => acc + (Number(item.score) || 0), 0) ?? 0;

    // RENDER

    return (
        <ContractProfileLayoutWrapper isLoading={loading || loadingTimeline}>
            <div className="space-y-6 py-4">
                <div className="flex items-center justify-between">
                    <Title as="h2" className="text-xl font-semibold">
                        Histórico de Puntuación
                    </Title>
                    {data?.score !== undefined && (
                        <div className="flex items-center gap-2">
                            <Text className="text-sm text-gray-500">Puntuación actual:</Text>
                            <div className={cn(
                                "px-3 py-1 rounded-full text-sm font-semibold",
                                netScore > 0 ? "bg-green-100 text-green-600" : 
                                netScore < 0 ? "bg-red-100 text-red-600" : 
                                "bg-gray-100 text-gray-600"
                            )}>
                                {netScore > 0 ? '+' : ''}{netScore}
                            </div>
                        </div>
                    )}
                </div>

                {timeline?.data?.length > 0 ? (
                    <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

                        <div className="space-y-6">
                            {timeline.data?.map((item: any) => (
                                <div key={item.id} className="relative flex items-start gap-4">
                                    {/* Timeline dot */}
                                    <div className={cn(
                                        "relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white shadow-sm",
                                        item.score > 0 ? "bg-green-100" : "bg-red-100"
                                    )}>
                                        <PiCircle className={cn(
                                            "h-3 w-3",
                                            item.score > 0 ? "text-green-600" : "text-red-600"
                                        )} />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0 pb-6">
                                        <div className={cn(
                                            "rounded-lg border p-4 transition-all hover:shadow-md",
                                            "bg-white border-gray-200"
                                        )}>
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                                                <div className="flex items-center gap-3">
                                                    <div className={cn(
                                                        "px-2 py-1 rounded text-sm font-semibold",
                                                        item.score > 0 ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                                                    )}>
                                                        {item.score > 0 ? '+' : ''}{item.score} puntos
                                                    </div>
                                                    <Text className="text-sm text-gray-500">
                                                        Contrato: {item.contract.code}
                                                    </Text>
                                                </div>
                                                <Text className="text-xs text-gray-400">
                                                    {moment(item.createdAt.date).format('DD/MM/YYYY HH:mm')}
                                                </Text>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                    <Text className="text-sm font-medium text-gray-700">
                                                        Comercial:
                                                    </Text>
                                                    <Text className="text-sm text-gray-600">
                                                        {
                                                            item.commercial ? 
                                                                item.commercial?.lastName ? 
                                                                `${item.commercial?.name || ''} ${item.commercial?.lastName || ''}` : 
                                                                item.commercial?.name || '' 
                                                            : 'No asignado'
                                                        } 
                                                        {
                                                            item.commercial ? 
                                                            `(${item.commercial?.email || ''})` : 
                                                            ''
                                                        }
                                                    </Text>
                                                </div>

                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                    <Text className="text-sm font-medium text-gray-700">
                                                        Empresa:
                                                    </Text>
                                                    <Text className="text-sm text-gray-600">
                                                        {item.company?.name}
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <PiCircle className="w-8 h-8 text-gray-400" />
                        </div>
                        <Title as="h3" className="text-lg font-medium text-gray-900 mb-2">
                            Sin histórico de puntuación
                        </Title>
                        <Text className="text-gray-500 max-w-sm">
                            No se han registrado cambios de puntuación para este contrato.
                        </Text>
                    </div>
                )}
            </div>
        </ContractProfileLayoutWrapper>
    );
};

export default ContractScoresHistory;